import React from "react";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import {
	AttentionBox,
	AttentionLabel,
	AttentionValue,
	CardLabel,
	CardValue,
	CloseButton,
	GridRow,
	InfoCard,
	LocalizedState,
	MetricCard,
	ModalBody,
	ModalPaper,
	ModalTitle,
	ModalTop,
	PainValue,
	SectionHeader,
	SectionTitle,
	SeeMoreButton,
	SkeletonLine,
	TitleContainer,
} from "./styles";
import { ReactComponent as CloseIcon } from "../../../../assets/kinologyIcons/svg/close.svg";
import { ReactComponent as FolderIcon } from "../../../../assets/kinologyIcons/svg/folder.svg";
import { ReactComponent as FileIcon } from "../../../../assets/kinologyIcons/svg/file-text.svg";
import { CopilotPatientReference } from "../../services/clinicalDashboardService";
import { CopilotSummarySnapshot } from "../AICopilotSection/useAICopilotViewModel";
import { useDetailedRecordModalViewModel } from "./useDetailedRecordModalViewModel";

const useStyles = makeStyles(() =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	})
);

interface DetailedRecordModalProps {
	isOpen: boolean;
	onClose: () => void;
	patient: CopilotPatientReference | null;
	copilotSummary?: CopilotSummarySnapshot | null;
}

const RecordLoading = () => (
	<div aria-busy="true" aria-label="Carregando perfil do paciente">
		<InfoCard>
			<SkeletonLine $width="32%" />
			<SkeletonLine $width="58%" />
		</InfoCard>
		<GridRow>
			<MetricCard>
				<SkeletonLine $width="68%" />
				<SkeletonLine $width="36%" />
			</MetricCard>
			<MetricCard>
				<SkeletonLine $width="68%" />
				<SkeletonLine $width="36%" />
			</MetricCard>
		</GridRow>
		<GridRow>
			<MetricCard>
				<SkeletonLine $width="28%" />
				<SkeletonLine $width="52%" />
			</MetricCard>
			<MetricCard>
				<SkeletonLine $width="38%" />
				<SkeletonLine $width="60%" />
			</MetricCard>
		</GridRow>
	</div>
);

const DetailedRecordModal: React.FC<DetailedRecordModalProps> = ({
	isOpen,
	onClose,
	patient,
	copilotSummary = null,
}) => {
	const classes = useStyles();
	const {
		view,
		recordLoading,
		recordError,
		attentionView,
		attentionLoading,
		attentionError,
		retryRecord,
		retryAttention,
		handleViewMapPain,
	} = useDetailedRecordModalViewModel(isOpen, patient, copilotSummary);
	return (
		<Modal
			aria-labelledby="detailed-record-modal-title"
			aria-describedby="detailed-record-modal-description"
			className={classes.modal}
			open={isOpen}
			onClose={onClose}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{ timeout: 300 }}
		>
			<Fade in={isOpen}>
				<ModalPaper>
					<ModalTop>
						<TitleContainer>
							<FolderIcon />
							<ModalTitle id="detailed-record-modal-title">
								Prontuário Detalhado
							</ModalTitle>
						</TitleContainer>
						<CloseButton type="button" onClick={onClose} aria-label="Fechar">
							<CloseIcon />
						</CloseButton>
					</ModalTop>

					<ModalBody id="detailed-record-modal-description">
						<SectionHeader>
							<FileIcon />
							<SectionTitle>Anamnese e contexto</SectionTitle>
						</SectionHeader>

						{recordLoading ? (
							<RecordLoading />
						) : recordError ? (
							<LocalizedState role="alert">
								<p>{recordError}</p>
								<button type="button" onClick={retryRecord}>
									Tentar novamente
								</button>
							</LocalizedState>
						) : !view ? (
							<LocalizedState>
								<p>Prontuário indisponível para este paciente.</p>
							</LocalizedState>
						) : (
							<>
								<InfoCard>
									<CardLabel>Perfil do paciente</CardLabel>
									<CardValue>{view.profile}</CardValue>
								</InfoCard>
								<GridRow>
									<MetricCard>
										<CardLabel>Lado dominante de membros superiores</CardLabel>
										<CardValue>{view.upperDominance}</CardValue>
									</MetricCard>
									<MetricCard>
										<CardLabel>Lado dominante de membros inferiores</CardLabel>
										<CardValue>{view.lowerDominance}</CardValue>
									</MetricCard>
								</GridRow>
								<GridRow>
									<MetricCard>
										<CardLabel>Mapa de dor</CardLabel>
										<PainValue
											$highlight={view.painStatus === "AVALIADA_COM_DOR"}
										>
											{view.pain}
										</PainValue>
										<SeeMoreButton
											onClick={() => {
												handleViewMapPain();
											}}
										>
											Ver mais
										</SeeMoreButton>
									</MetricCard>
									<MetricCard>
										<CardLabel>{view.activityLabel}</CardLabel>
										<CardValue>{view.activity}</CardValue>
										{view.activityDate && (
											<small>Realizada em {view.activityDate}</small>
										)}
									</MetricCard>
								</GridRow>
							</>
						)}

						{attentionLoading ? (
							<AttentionBox aria-busy="true" $tone="NEUTRAL">
								<SkeletonLine $width="24%" />
								<SkeletonLine $width="72%" />
							</AttentionBox>
						) : attentionError ? (
							<AttentionBox role="alert" $tone="NEUTRAL">
								<AttentionLabel $tone="NEUTRAL">
									Pontos de atenção
								</AttentionLabel>
								<AttentionValue $tone="NEUTRAL">
									{attentionError}
								</AttentionValue>
								<button type="button" onClick={retryAttention}>
									Tentar novamente
								</button>
							</AttentionBox>
						) : (
							<AttentionBox $tone={attentionView.level || attentionView.status}>
								<AttentionLabel
									$tone={attentionView.level || attentionView.status}
								>
									Pontos de atenção
								</AttentionLabel>
								<AttentionValue
									$tone={attentionView.level || attentionView.status}
								>
									{attentionView.text}
								</AttentionValue>
							</AttentionBox>
						)}
					</ModalBody>
				</ModalPaper>
			</Fade>
		</Modal>
	);
};

export default DetailedRecordModal;
